import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Save, Eye, EyeOff, Webhook, Mail, CheckCircle, XCircle, MessageSquare, Code, BarChart3, Facebook, Database, Plus, Trash2, Send, Loader2, Smartphone, Sparkles, Clock, Calendar, RefreshCw } from "lucide-react";

type WhatsAppProvider = "goapi" | "evolution";
interface WhatsAppInstance {
  id: string;
  label: string;
  provider: WhatsAppProvider;
  url: string;
  instance: string;
  token: string;
  phone: string;
  active: boolean;
}
type RouteKey = "booking_client" | "booking_admin" | "order_client" | "order_admin" | "contact_admin" | "pdv_client" | "client_record_staff";
type WhatsAppRouting = Partial<Record<RouteKey, string | null>>;

const ROUTE_LABELS: { key: RouteKey; label: string; desc: string }[] = [
  { key: "booking_client", label: "Reservas, Cliente", desc: "Envia notificações de reserva para o cliente" },
  { key: "booking_admin", label: "Reservas, Admin/Colaborador", desc: "Envia notificações de reserva para o colaborador" },
  { key: "order_client", label: "Encomendas, Cliente", desc: "Envia atualizações de encomenda para o cliente" },
  { key: "order_admin", label: "Encomendas, Admin", desc: "Envia notificações de encomenda para o admin" },
  { key: "contact_admin", label: "Contactos do site, Admin", desc: "Envia mensagens do formulário de contacto" },
  { key: "pdv_client", label: "PDV / Recibos, Cliente", desc: "Envia recibos de venda no PDV" },
  { key: "client_record_staff", label: "Recebimento de Ficha, Colaborador", desc: "Envia resumo da ficha do cliente para o colaborador" },
];

const newInstance = (): WhatsAppInstance => ({
  id: (globalThis.crypto?.randomUUID?.() ?? String(Date.now() + Math.random())),
  label: "",
  provider: "goapi",
  url: "",
  instance: "",
  token: "",
  phone: "",
  active: true,
});


interface ApiConfig {
  evolution_api_url: string;
  evolution_api_instance: string;
  evolution_api_key: string;
  goapi_url: string;
  goapi_instance: string;
  goapi_token: string;
  smtp_host: string;
  smtp_port: string;
  smtp_user: string;
  smtp_password: string;
  smtp_from_email: string;
  smtp_from_name: string;
}

const bookingStatuses = [
  { key: "pending", label: "Pendente", desc: "Quando o cliente faz uma nova reserva" },

  { key: "confirmed", label: "Confirmado", desc: "Quando a reserva é confirmada" },
  { key: "on_the_way", label: "A Caminho", desc: "Quando o colaborador está a caminho" },
  { key: "cancelled", label: "Cancelado", desc: "Quando a reserva é cancelada" },
  { key: "rejected", label: "Rejeitado", desc: "Quando a reserva é rejeitada" },
  { key: "completed", label: "Concluído", desc: "Quando o serviço é finalizado" },
  { key: "reminder_60min", label: "Lembrete 60min", desc: "Enviado 60 minutos antes da sessão" },
  { key: "reminder_1day", label: "Lembrete 1 dia", desc: "Enviado 1 dia antes (reservas com 5+ dias de antecedência)" },
];

const orderStatuses = [
  { key: "pending", label: "Pendente", desc: "Quando o cliente faz uma nova encomenda" },
  { key: "confirmed", label: "Confirmado", desc: "Quando a encomenda é confirmada" },
  { key: "shipped", label: "Enviado", desc: "Quando a encomenda é enviada" },
  { key: "delivered", label: "Entregue", desc: "Quando a encomenda é entregue" },
  { key: "cancelled", label: "Cancelado", desc: "Quando a encomenda é cancelada" },
];
interface ScheduleCfg {
  mode: "immediate" | "delay" | "time_today" | "time_next_day";
  delay_minutes?: number;
  time?: string;
}
interface StatusMessages {
  [key: string]: { whatsapp: string; email_subject: string; email_body: string };
}

const defaultConfig: ApiConfig = {
  evolution_api_url: "",
  evolution_api_instance: "",
  evolution_api_key: "",
  goapi_url: "",
  goapi_instance: "",
  goapi_token: "",
  smtp_host: "",
  smtp_port: "587",
  smtp_user: "",
  smtp_password: "",
  smtp_from_email: "",
  smtp_from_name: "",
};

const defaultBookingMessages: StatusMessages = {
  pending: { whatsapp: "Olá {{nome}}! 🙏 A sua reserva para {{servico}} no dia {{data}} às {{hora}} foi recebida. Aguarde confirmação.", email_subject: "Reserva recebida, {{servico}}", email_body: "Olá {{nome}},\n\nRecebemos a sua reserva para {{servico}} no dia {{data}} às {{hora}}.\n\nEntraremos em contacto brevemente para confirmação.\n\nObrigado!" },
  confirmed: { whatsapp: "Olá {{nome}}!  A sua reserva para {{servico}} no dia {{data}} às {{hora}} foi confirmada. Até breve!", email_subject: "Reserva confirmada, {{servico}}", email_body: "Olá {{nome}},\n\nA sua reserva para {{servico}} no dia {{data}} às {{hora}} foi confirmada.\n\nEstamos à sua espera!\n\nObrigado!" },
  on_the_way: { whatsapp: "Olá {{nome}}! 🚗 Estou a caminho para a sua sessão de {{servico}}. Prepare um espaço confortável. Até já! 🌿", email_subject: "A caminho, {{servico}}", email_body: "Olá {{nome}},\n\nA terapeuta já está a caminho para a sua sessão de {{servico}}.\n\nPrepare um espaço confortável e aguarde.\n\nAté já!\nMente Sã" },
  cancelled: { whatsapp: "Olá {{nome}}, a sua reserva para {{servico}} no dia {{data}} foi cancelada. Se precisar, contacte-nos.", email_subject: "Reserva cancelada, {{servico}}", email_body: "Olá {{nome}},\n\nA sua reserva para {{servico}} no dia {{data}} foi cancelada.\n\nSe precisar de reagendar, não hesite em contactar-nos." },
  rejected: { whatsapp: "Olá {{nome}}, infelizmente não foi possível confirmar a sua reserva para {{servico}} no dia {{data}}. Motivo: {{motivo}}", email_subject: "Reserva não confirmada, {{servico}}", email_body: "Olá {{nome}},\n\nInfelizmente não foi possível confirmar a sua reserva para {{servico}} no dia {{data}}.\n\nMotivo: {{motivo}}\n\nPedimos desculpa pelo incómodo." },
  completed: { whatsapp: "Olá {{nome}}! 🌟 Obrigado pela sua visita! Esperamos que tenha gostado do serviço {{servico}}. Até breve!", email_subject: "Obrigado pela sua visita!", email_body: "Olá {{nome}},\n\nObrigado por nos visitar! Esperamos que tenha gostado do serviço {{servico}}.\n\nFicaríamos muito felizes com o seu feedback.\n\nAté breve!" },
  reminder_60min: { whatsapp: "Olá {{nome}}!  Lembrete: a sua sessão de {{servico}} é HOJE às {{hora}}. Até já!", email_subject: "Lembrete, Sessão hoje às {{hora}}", email_body: "Olá {{nome}},\n\nEste é um lembrete de que a sua sessão de {{servico}} é hoje às {{hora}}.\n\nAté já!\nMente Sã" },
  reminder_1day: { whatsapp: "Olá {{nome}}!  Lembrete: a sua sessão de {{servico}} é amanhã, {{data}} às {{hora}}. Até lá!", email_subject: "Lembrete, Sessão amanhã às {{hora}}", email_body: "Olá {{nome}},\n\nEste é um lembrete de que a sua sessão de {{servico}} é amanhã, {{data}} às {{hora}}.\n\nAté lá!\nMente Sã" },
};

const defaultOrderMessages: StatusMessages = {
  pending: { whatsapp: "Olá {{nome}}! 🛒 A sua encomenda #{{pedido}} foi recebida. Valor: {{valor}}€. Aguarde confirmação.", email_subject: "Encomenda recebida, #{{pedido}}", email_body: "Olá {{nome}},\n\nRecebemos a sua encomenda #{{pedido}} no valor de {{valor}}€.\n\nEntraremos em contacto brevemente.\n\nObrigado!" },
  confirmed: { whatsapp: "Olá {{nome}}!  A sua encomenda #{{pedido}} foi confirmada e está a ser preparada.", email_subject: "Encomenda confirmada, #{{pedido}}", email_body: "Olá {{nome}},\n\nA sua encomenda #{{pedido}} foi confirmada e está a ser preparada.\n\nObrigado pela sua compra!" },
  shipped: { whatsapp: "Olá {{nome}}!  A sua encomenda #{{pedido}} foi enviada!", email_subject: "Encomenda enviada, #{{pedido}}", email_body: "Olá {{nome}},\n\nA sua encomenda #{{pedido}} foi enviada!\n\nObrigado!" },
  delivered: { whatsapp: "Olá {{nome}}! 🎉 A sua encomenda #{{pedido}} foi entregue. Obrigado pela preferência!", email_subject: "Encomenda entregue, #{{pedido}}", email_body: "Olá {{nome}},\n\nA sua encomenda #{{pedido}} foi entregue.\n\nEsperamos que goste! Obrigado pela preferência!" },
  cancelled: { whatsapp: "Olá {{nome}}, a sua encomenda #{{pedido}} foi cancelada. Contacte-nos se tiver dúvidas.", email_subject: "Encomenda cancelada, #{{pedido}}", email_body: "Olá {{nome}},\n\nA sua encomenda #{{pedido}} foi cancelada.\n\nSe tiver alguma dúvida, não hesite em contactar-nos." },
};

const variableHints: Record<string, string> = {
  booking: "Variáveis: {{nome}}, {{servico}}, {{data}}, {{hora}}, {{motivo}}, {{colaborador}}, {{eta}}, {{valor}}, {{local}}",
  order: "Variáveis: {{nome}}, {{pedido}}, {{valor}}, {{metodo_entrega}}",
};

  // Tracking Tag interface
  interface TrackingTag {
    id: string;
    name: string;
    code: string;
    placement: "head" | "body";
    enabled: boolean;
  }

const ApiSettings = () => {
  const [config, setConfig] = useState<ApiConfig>(defaultConfig);
  const [bookingMessages, setBookingMessages] = useState<StatusMessages>(defaultBookingMessages);
  const [orderMessages, setOrderMessages] = useState<StatusMessages>(defaultOrderMessages);
  const [schedules, setSchedules] = useState<Record<string, ScheduleCfg>>({});
  const [saving, setSaving] = useState(false);
  const [showEvolutionKey, setShowEvolutionKey] = useState(false);
  const [showGoApiToken, setShowGoApiToken] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState<null | string>(null);
  const [instances, setInstances] = useState<WhatsAppInstance[]>([]);
  const [routing, setRouting] = useState<WhatsAppRouting>({});
  const [showToken, setShowToken] = useState<Record<string, boolean>>({});
  // Google Calendar
  const [gcalConnected, setGcalConnected] = useState(false);
  const [gcalSyncing, setGcalSyncing] = useState(false);
  const [gcalLastSync, setGcalLastSync] = useState<string | null>(null);
  // Groq AI
  const [groqApiKey, setGroqApiKey] = useState("");
  const [showGroqKey, setShowGroqKey] = useState(false);
  const [groqModel, setGroqModel] = useState("");
  const [groqModels, setGroqModels] = useState<{ id: string; tier: string; context_window?: number }[]>([]);
  const [groqLoading, setGroqLoading] = useState(false);
  const [groqValidated, setGroqValidated] = useState(false);
  const { toast } = useToast();

  const runTest = async (type: "smtp") => {
    const recipient = testEmail.trim();
    if (!recipient) { toast({ title: "Indique um email de teste", variant: "destructive" }); return; }
    const payloadCfg = { host: config.smtp_host, port: config.smtp_port, user: config.smtp_user, password: config.smtp_password, fromEmail: config.smtp_from_email, fromName: config.smtp_from_name };
    setTesting(type);
    try {
      const { data, error } = await supabase.functions.invoke("test-integration", { body: { type, config: payloadCfg, recipient } });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Falha desconhecida");
      toast({ title: " Teste enviado com sucesso!", description: `Email enviado para ${recipient}` });
    } catch (e: any) {
      toast({ title: " Falha no teste", description: e?.message || String(e), variant: "destructive" });
    } finally { setTesting(null); }
  };

  const runTestInstance = async (inst: WhatsAppInstance) => {
    const recipient = (inst.phone || testPhone).trim();
    if (!recipient) { toast({ title: "Indique um número de teste (na instância ou no campo)", variant: "destructive" }); return; }
    if (!inst.url || !inst.token) { toast({ title: "Preencha URL e Token da instância", variant: "destructive" }); return; }
    setTesting(inst.id);
    try {
      const payloadCfg = { url: inst.url, instance: inst.instance, token: inst.token, apiKey: inst.token };
      const { data, error } = await supabase.functions.invoke("test-integration", { body: { provider: inst.provider, config: payloadCfg, recipient } });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Falha desconhecida");
      toast({ title: " Teste enviado!", description: `Mensagem enviada via ${inst.label || inst.provider} para ${recipient}` });
    } catch (e: any) {
      toast({ title: " Falha no teste", description: e?.message || String(e), variant: "destructive" });
    } finally { setTesting(null); }
  };

  const updateInstance = (id: string, patch: Partial<WhatsAppInstance>) => {
    setInstances((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  };
  const addInstance = () => setInstances((prev) => [...prev, { ...newInstance(), label: `Instância ${prev.length + 1}` }]);
  const removeInstance = (id: string) => {
    setInstances((prev) => prev.filter((i) => i.id !== id));
    setRouting((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(next) as RouteKey[]) if (next[k] === id) next[k] = null;
      return next;
    });
  };


  // Tracking scripts state
  const [gaId, setGaId] = useState("");
  const [metaPixelId, setMetaPixelId] = useState("");
  const [tiktokPixelId, setTiktokPixelId] = useState("");
  const [customHeadScripts, setCustomHeadScripts] = useState("");
  const [customBodyScripts, setCustomBodyScripts] = useState("");
  const [trackingTags, setTrackingTags] = useState<TrackingTag[]>([]);

  // Database connection state
  const [dbUrl, setDbUrl] = useState("");
  const [dbAnonKey, setDbAnonKey] = useState("");
  const [showDbKey, setShowDbKey] = useState(false);
  const [customDbConnections, setCustomDbConnections] = useState<{ id: string; name: string; url: string; key: string }[]>([]);

  useEffect(() => {
    const allKeys = [
      ...Object.keys(defaultConfig),
      "booking_status_messages", "order_status_messages", "status_message_schedules",
      "google_analytics_id", "meta_pixel_id", "tiktok_pixel_id",
      "custom_head_scripts", "custom_body_scripts", "tracking_tags",
      "db_connection_url", "db_connection_key", "custom_db_connections",
      "whatsapp_instances", "whatsapp_routing",
      "groq_api_key", "groq_model",
      "google_calendar_tokens", "google_calendar_last_sync",
    ];
    supabase.from("app_settings").select("key, value").in("key", allKeys).then(({ data }) => {
      if (data) {
        const loaded = { ...defaultConfig };
        data.forEach((row) => {
          if (row.key === "groq_api_key") {
            const v = typeof row.value === "string" ? row.value : String(row.value ?? "");
            setGroqApiKey(v);
            if (v) setGroqValidated(true);
          } else if (row.key === "groq_model") {
            setGroqModel(typeof row.value === "string" ? row.value : String(row.value ?? ""));
          } else if (row.key === "status_message_schedules" && row.value) {
            try {
              const p = typeof row.value === "string" ? JSON.parse(row.value) : row.value;
              if (p && typeof p === "object") setSchedules(p as Record<string, ScheduleCfg>);
            } catch {}
          } else if (row.key === "booking_status_messages" && row.value) {
            setBookingMessages({ ...defaultBookingMessages, ...(row.value as unknown as StatusMessages) });
          } else if (row.key === "order_status_messages" && row.value) {
            setOrderMessages({ ...defaultOrderMessages, ...(row.value as unknown as StatusMessages) });
          } else if (row.key === "google_analytics_id") {
            setGaId(typeof row.value === "string" ? row.value : String(row.value ?? ""));
          } else if (row.key === "meta_pixel_id") {
            setMetaPixelId(typeof row.value === "string" ? row.value : String(row.value ?? ""));
          } else if (row.key === "tiktok_pixel_id") {
            setTiktokPixelId(typeof row.value === "string" ? row.value : String(row.value ?? ""));
          } else if (row.key === "custom_head_scripts") {
            setCustomHeadScripts(typeof row.value === "string" ? row.value : String(row.value ?? ""));
          } else if (row.key === "custom_body_scripts") {
            setCustomBodyScripts(typeof row.value === "string" ? row.value : String(row.value ?? ""));
          } else if (row.key === "tracking_tags" && row.value) {
            try { const p = typeof row.value === "string" ? JSON.parse(row.value) : row.value; if (Array.isArray(p)) setTrackingTags(p); } catch {}
          } else if (row.key === "db_connection_url") {
            setDbUrl(typeof row.value === "string" ? row.value : String(row.value ?? ""));
          } else if (row.key === "db_connection_key") {
            setDbAnonKey(typeof row.value === "string" ? row.value : String(row.value ?? ""));
          } else if (row.key === "custom_db_connections" && row.value) {
            try { const p = typeof row.value === "string" ? JSON.parse(row.value) : row.value; if (Array.isArray(p)) setCustomDbConnections(p); } catch {}
          } else if (row.key === "whatsapp_instances" && row.value) {
            try { const p = typeof row.value === "string" ? JSON.parse(row.value) : row.value; if (Array.isArray(p)) setInstances(p as WhatsAppInstance[]); } catch {}
          } else if (row.key === "whatsapp_routing" && row.value) {
            try { const p = typeof row.value === "string" ? JSON.parse(row.value) : row.value; if (p && typeof p === "object") setRouting(p as WhatsAppRouting); } catch {}
          } else if (row.key === "google_calendar_tokens") {
            setGcalConnected(!!row.value);
          } else if (row.key === "google_calendar_last_sync") {
            setGcalLastSync(typeof row.value === "string" ? row.value : null);
          } else {
            const key = row.key as keyof ApiConfig;
            if (key in loaded) {
              const val = row.value;
              loaded[key] = typeof val === "string" ? val : JSON.stringify(val).replace(/^"|"$/g, "");
            }
          }
        });
        
        // Handle OAuth callback
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");
        if (code) {
          supabase.functions.invoke("google-calendar-sync", {
            body: { action: "exchange_code", code, redirect_uri: `${window.location.origin}/admin/api` }
          }).then(({ error }) => {
            if (!error) {
              setGcalConnected(true);
              toast({ title: "Google Calendar ligado com sucesso!" });
              window.history.replaceState({}, document.title, window.location.pathname);
            }
          });
        }

        setConfig(loaded);

        // Migração legacy: se ainda não existirem instâncias mas houver configuração antiga, gera automaticamente
        const hasNewInstances = data.some((r) => r.key === "whatsapp_instances");
        if (!hasNewInstances) {
          const migrated: WhatsAppInstance[] = [];
          if (loaded.goapi_url && loaded.goapi_token) {
            migrated.push({ ...newInstance(), label: "GoAPI (migrada)", provider: "goapi", url: loaded.goapi_url, instance: loaded.goapi_instance, token: loaded.goapi_token });
          }
          if (loaded.evolution_api_url && loaded.evolution_api_key && loaded.evolution_api_instance) {
            migrated.push({ ...newInstance(), label: "Evolution (migrada)", provider: "evolution", url: loaded.evolution_api_url, instance: loaded.evolution_api_instance, token: loaded.evolution_api_key });
          }
          if (migrated.length) {
            setInstances(migrated);
            const first = migrated[0].id;
            setRouting({ booking_client: first, booking_admin: first, order_client: first, order_admin: first, contact_admin: first, pdv_client: first });
          }
        }
      }
    });
  }, []);


  const handleSave = async () => {
    setSaving(true);
    const entries = Object.entries(config);
    for (const [key, value] of entries) {
      await supabase
        .from("app_settings")
        .upsert({ key, value: value as any }, { onConflict: "key" });
    }
    await supabase.from("app_settings").upsert(
      { key: "status_message_schedules", value: schedules as any },
      { onConflict: "key" }
    );
    await supabase.from("app_settings").upsert(
      { key: "booking_status_messages", value: bookingMessages as any },
      { onConflict: "key" }
    );
    await supabase.from("app_settings").upsert(
      { key: "order_status_messages", value: orderMessages as any },
      { onConflict: "key" }
    );
    // Tracking scripts
    for (const [key, value] of [
      ["google_analytics_id", gaId],
      ["meta_pixel_id", metaPixelId],
      ["tiktok_pixel_id", tiktokPixelId],
      ["custom_head_scripts", customHeadScripts],
      ["custom_body_scripts", customBodyScripts],
    ] as [string, string][]) {
      await supabase.from("app_settings").upsert({ key, value: value as any }, { onConflict: "key" });
    }
    // Tracking tags
    await supabase.from("app_settings").upsert({ key: "tracking_tags", value: JSON.stringify(trackingTags) as any }, { onConflict: "key" });
    // DB connections
    await supabase.from("app_settings").upsert({ key: "db_connection_url", value: dbUrl as any }, { onConflict: "key" });
    await supabase.from("app_settings").upsert({ key: "db_connection_key", value: dbAnonKey as any }, { onConflict: "key" });
    await supabase.from("app_settings").upsert({ key: "custom_db_connections", value: JSON.stringify(customDbConnections) as any }, { onConflict: "key" });
    // WhatsApp instances + routing
    await supabase.from("app_settings").upsert({ key: "whatsapp_instances", value: instances as any }, { onConflict: "key" });
    await supabase.from("app_settings").upsert({ key: "whatsapp_routing", value: routing as any }, { onConflict: "key" });
    // Groq AI
    await supabase.from("app_settings").upsert({ key: "groq_api_key", value: groqApiKey as any }, { onConflict: "key" });
    await supabase.from("app_settings").upsert({ key: "groq_model", value: groqModel as any }, { onConflict: "key" });
    toast({ title: "Configurações guardadas com sucesso!" });
    setSaving(false);
  };

  const validateGroq = async () => {
    if (!groqApiKey.trim()) {
      toast({ title: "Introduza a API Key da Groq", variant: "destructive" });
      return;
    }
    setGroqLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("groq-models", {
        body: { apiKey: groqApiKey.trim() },
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Falha a validar chave");
      const models = data.models || [];
      setGroqModels(models);
      setGroqValidated(true);
      if (!groqModel && models.length) setGroqModel(models[0].id);
      // Persistir imediatamente a chave para reutilização pela função translate
      await supabase.from("app_settings").upsert({ key: "groq_api_key", value: groqApiKey.trim() as any }, { onConflict: "key" });
      toast({ title: ` Chave válida, ${models.length} modelos disponíveis` });
    } catch (e: any) {
      setGroqValidated(false);
      toast({ title: " Falha na validação", description: e?.message || String(e), variant: "destructive" });
    } finally {
      setGroqLoading(false);
    }
  };

  const updateField = (field: keyof ApiConfig, value: string) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const updateBookingMsg = (status: string, field: string, value: string) => {
    setBookingMessages((prev) => ({
      ...prev,
      [status]: { ...prev[status], [field]: value },
    }));
  };

  const updateOrderMsg = (status: string, field: string, value: string) => {
    setOrderMessages((prev) => ({
      ...prev,
      [status]: { ...prev[status], [field]: value },
    }));
  };

  const updateSchedule = (status: string, patch: Partial<ScheduleCfg>) => {
    setSchedules((prev) => ({
      ...prev,
      [status]: { mode: "immediate", delay_minutes: 60, time: "20:00", ...prev[status], ...patch },
    }));
  };

  const renderMessageFields = (
    statuses: typeof bookingStatuses,
    messages: StatusMessages,
    updateFn: (status: string, field: string, value: string) => void,
    type: "booking" | "order"
  ) => (
    <div className="space-y-6">
      <p className="text-xs text-muted-foreground">{variableHints[type]}</p>
      {statuses.map((s) => (
        <div key={s.key} className="rounded-xl border border-border p-4 space-y-3">
          <div>
            <h4 className="font-semibold text-sm">{s.label}</h4>
            <p className="text-xs text-muted-foreground">{s.desc}</p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1"><Webhook size={12} className="text-green-600" /> WhatsApp</Label>
            <Textarea
              value={messages[s.key]?.whatsapp || ""}
              onChange={(e) => updateFn(s.key, "whatsapp", e.target.value)}
              rows={2}
              className="text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1"><Mail size={12} className="text-blue-600" /> Assunto do Email</Label>
            <Input
              value={messages[s.key]?.email_subject || ""}
              onChange={(e) => updateFn(s.key, "email_subject", e.target.value)}
              className="text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1"><Mail size={12} className="text-blue-600" /> Corpo do Email</Label>
            <Textarea
              value={messages[s.key]?.email_body || ""}
              onChange={(e) => updateFn(s.key, "email_body", e.target.value)}
              rows={4}
              className="text-sm"
            />
          </div>

          {type === "booking" && (
            <div className="rounded-lg border border-border/60 bg-muted/30 p-3 space-y-2">
              <Label className="text-xs flex items-center gap-1">
                <Clock size={12} className="text-primary" /> Momento do envio
              </Label>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={schedules[s.key]?.mode || "immediate"}
                  onChange={(e) => updateSchedule(s.key, { mode: e.target.value as ScheduleCfg["mode"] })}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                >
                  <option value="immediate">Imediatamente</option>
                  <option value="delay">Após X minutos</option>
                  <option value="time_today">A uma hora do próprio dia</option>
                  <option value="time_next_day">A uma hora do dia seguinte</option>
                </select>

                {schedules[s.key]?.mode === "delay" && (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={1}
                      value={schedules[s.key]?.delay_minutes ?? 60}
                      onChange={(e) => updateSchedule(s.key, { delay_minutes: parseInt(e.target.value, 10) || 1 })}
                      className="h-9 w-24 text-sm"
                    />
                    <span className="text-xs text-muted-foreground">minutos depois</span>
                  </div>
                )}

                {(schedules[s.key]?.mode === "time_today" || schedules[s.key]?.mode === "time_next_day") && (
                  <Input
                    type="time"
                    value={schedules[s.key]?.time || "20:00"}
                    onChange={(e) => updateSchedule(s.key, { time: e.target.value })}
                    className="h-9 w-32 text-sm"
                  />
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Hora de Lisboa. Se a hora do próprio dia já tiver passado, o envio é feito à mesma hora no dia seguinte.
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  );


  return (
    <div>
      <h1 className="font-display text-3xl font-bold mb-8">Integrações / APIs</h1>

      <Tabs defaultValue="connections" className="max-w-2xl">
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="connections" className="gap-2 rounded-full px-4">Ligações</TabsTrigger>
          <TabsTrigger value="gcal" className="gap-2 rounded-full px-4"><Calendar size={14} /> Google Calendar</TabsTrigger>
          <TabsTrigger value="tracking" className="gap-2 rounded-full px-4">Scripts / Tracking</TabsTrigger>
          <TabsTrigger value="database" className="gap-2 rounded-full px-4">Base de Dados</TabsTrigger>
          <TabsTrigger value="booking_messages" className="gap-2 rounded-full px-4">Mensagens Reservas</TabsTrigger>
          <TabsTrigger value="order_messages" className="gap-2 rounded-full px-4">Mensagens Encomendas</TabsTrigger>
          <TabsTrigger value="ai" className="gap-2 rounded-full px-4">IA / Tradução</TabsTrigger>
        </TabsList>

        <TabsContent value="connections" className="space-y-8">
          {/* WhatsApp, Instâncias dinâmicas */}
          <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <Smartphone size={20} className="text-emerald-600" />
                </div>
                <div>
                  <h2 className="font-display text-xl font-semibold">Instâncias WhatsApp</h2>
                  <p className="text-xs text-muted-foreground">
                    Adicione quantas contas/números quiser (GoAPI ou Evolution). Depois escolha em "Roteamento" qual envia para cada sector.
                  </p>
                </div>
              </div>
              <Button size="sm" onClick={addInstance} className="gap-1">
                <Plus size={14} /> Adicionar instância
              </Button>
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-border/50">
              <Label className="text-xs">Nº teste global (fallback):</Label>
              <Input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="351..."
                className="h-9 w-52 text-xs"
              />
            </div>

            {instances.length === 0 && (
              <div className="text-sm text-muted-foreground border border-dashed border-border rounded-xl p-6 text-center">
                Nenhuma instância cadastrada. Clique em "Adicionar instância" para começar.
              </div>
            )}

            <div className="space-y-4">
              {instances.map((inst) => {
                const configured = !!(inst.url && inst.token && (inst.provider === "goapi" ? true : inst.instance));
                return (
                  <div key={inst.id} className="rounded-xl border border-border bg-background p-4 space-y-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <Input
                        value={inst.label}
                        onChange={(e) => updateInstance(inst.id, { label: e.target.value })}
                        placeholder="Rótulo (ex.: Thaiane Brasil)"
                        className="flex-1 min-w-[180px] font-medium"
                      />
                      <Select value={inst.provider} onValueChange={(v) => updateInstance(inst.id, { provider: v as WhatsAppProvider })}>
                        <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="goapi">GoAPI / Evolution GO</SelectItem>
                          <SelectItem value="evolution">Evolution API</SelectItem>
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-2 text-xs text-muted-foreground">
                        <input
                          type="checkbox"
                          checked={inst.active}
                          onChange={(e) => updateInstance(inst.id, { active: e.target.checked })}
                        />
                        Ativa
                      </label>
                      <Button variant="ghost" size="icon" onClick={() => removeInstance(inst.id)} className="text-destructive">
                        <Trash2 size={16} />
                      </Button>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">URL Base</Label>
                        <Input value={inst.url} onChange={(e) => updateInstance(inst.id, { url: e.target.value })} placeholder="https://api.exemplo.com" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">
                          {inst.provider === "evolution" ? "Instância (obrigatório)" : "Instância / Sessão (opcional)"}
                        </Label>
                        <Input value={inst.instance} onChange={(e) => updateInstance(inst.id, { instance: e.target.value })} placeholder={inst.provider === "evolution" ? "minha-instancia" : "default"} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Token / API Key</Label>
                        <div className="flex gap-2">
                          <Input
                            type={showToken[inst.id] ? "text" : "password"}
                            value={inst.token}
                            onChange={(e) => updateInstance(inst.id, { token: e.target.value })}
                            placeholder="********"
                            className="flex-1"
                          />
                          <Button type="button" variant="outline" size="icon" onClick={() => setShowToken((p) => ({ ...p, [inst.id]: !p[inst.id] }))}>
                            {showToken[inst.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Nº conectado (informativo/teste)</Label>
                        <Input value={inst.phone} onChange={(e) => updateInstance(inst.id, { phone: e.target.value })} placeholder="351915883495" />
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-border/50">
                      <div className="flex items-center gap-2 text-xs">
                        {configured ? (
                          <><CheckCircle size={14} className="text-green-500" /><span className="text-green-600">Configurada</span></>
                        ) : (
                          <><XCircle size={14} className="text-muted-foreground" /><span className="text-muted-foreground">Incompleta</span></>
                        )}
                      </div>
                      <Button size="sm" variant="outline" onClick={() => runTestInstance(inst)} disabled={testing !== null} className="gap-1">
                        {testing === inst.id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                        Testar envio
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Roteamento por sector */}
          <section className="rounded-2xl border border-border bg-card p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Webhook size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold">Roteamento de notificações</h2>
                <p className="text-xs text-muted-foreground">Escolha qual instância envia cada tipo de mensagem. Se falhar, o sistema tenta automaticamente outra instância ativa.</p>
              </div>
            </div>
            {instances.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Adicione pelo menos uma instância acima para configurar o roteamento.</p>
            ) : (
              <div className="grid gap-3">
                {ROUTE_LABELS.map((r) => (
                  <div key={r.key} className="flex flex-wrap items-center gap-3 rounded-xl border border-border p-3">
                    <div className="flex-1 min-w-[240px]">
                      <div className="text-sm font-medium">{r.label}</div>
                      <div className="text-xs text-muted-foreground">{r.desc}</div>
                    </div>
                    <Select
                      value={routing[r.key] ?? "__none"}
                      onValueChange={(v) => setRouting((prev) => ({ ...prev, [r.key]: v === "__none" ? null : v }))}
                    >
                      <SelectTrigger className="w-72"><SelectValue placeholder="Não enviar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Não enviar</SelectItem>
                        {instances.map((i) => (
                          <SelectItem key={i.id} value={i.id}>
                            {(i.label || i.provider)} {i.phone ? `· ${i.phone}` : ""} {i.active ? "" : "(inativa)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}
          </section>



          {/* SMTP */}
          <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Mail size={20} className="text-blue-600" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold">SMTP, Email</h2>
                <p className="text-xs text-muted-foreground">Envio de emails de confirmação e notificação</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp-host">Servidor SMTP</Label>
                <Input id="smtp-host" value={config.smtp_host} onChange={(e) => updateField("smtp_host", e.target.value)} placeholder="smtp.gmail.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp-port">Porta</Label>
                <Input id="smtp-port" value={config.smtp_port} onChange={(e) => updateField("smtp_port", e.target.value)} placeholder="587" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp-user">Utilizador</Label>
                <Input id="smtp-user" value={config.smtp_user} onChange={(e) => updateField("smtp_user", e.target.value)} placeholder="email@dominio.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp-pass">Palavra-passe</Label>
                <div className="flex gap-2">
                  <Input id="smtp-pass" type={showSmtpPassword ? "text" : "password"} value={config.smtp_password} onChange={(e) => updateField("smtp_password", e.target.value)} placeholder="********" className="flex-1" />
                  <Button type="button" variant="outline" size="icon" onClick={() => setShowSmtpPassword(!showSmtpPassword)}>
                    {showSmtpPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp-from-email">Email remetente</Label>
                <Input id="smtp-from-email" value={config.smtp_from_email} onChange={(e) => updateField("smtp_from_email", e.target.value)} placeholder="noreply@mentesa.pt" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp-from-name">Nome remetente</Label>
                <Input id="smtp-from-name" value={config.smtp_from_name} onChange={(e) => updateField("smtp_from_name", e.target.value)} placeholder="Mente Sã" />
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 pt-2 border-t border-border/50">
              <div className="flex items-center gap-2 text-xs">
                {config.smtp_host && config.smtp_user ? (
                  <><CheckCircle size={14} className="text-green-500" /><span className="text-green-600">Configurado</span></>
                ) : (
                  <><XCircle size={14} className="text-muted-foreground" /><span className="text-muted-foreground">Não configurado</span></>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="email@teste.com"
                  className="h-9 w-56 text-xs"
                />
                <Button size="sm" variant="outline" onClick={() => runTest("smtp")} disabled={testing !== null} className="gap-1">
                  {testing === "smtp" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Testar envio
                </Button>
              </div>
            </div>
          </section>
        </TabsContent>

        <TabsContent value="booking_messages">
          <section className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <MessageSquare size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold">Mensagens de Reservas</h2>
                <p className="text-xs text-muted-foreground">Templates enviados por WhatsApp e Email em cada mudança de status</p>
              </div>
            </div>
            {renderMessageFields(bookingStatuses, bookingMessages, updateBookingMsg, "booking")}
          </section>
        </TabsContent>

        <TabsContent value="order_messages">
          <section className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <MessageSquare size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold">Mensagens de Encomendas</h2>
                <p className="text-xs text-muted-foreground">Templates enviados por WhatsApp e Email em cada mudança de status</p>
              </div>
            </div>
            {renderMessageFields(orderStatuses, orderMessages, updateOrderMsg, "order")}
          </section>
        </TabsContent>

        {/* Google Calendar Tab */}
        <TabsContent value="gcal" className="space-y-8">
          <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Calendar size={20} className="text-blue-600" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold">Google Calendar</h2>
                <p className="text-xs text-muted-foreground">Sincronize as reservas do sistema com o seu Calendário Google</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted/30">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Estado da Ligação</p>
                  <div className="flex items-center gap-2">
                    {gcalConnected ? (
                      <><CheckCircle size={14} className="text-green-500" /><span className="text-xs text-green-600 font-medium">Ligado</span></>
                    ) : (
                      <><XCircle size={14} className="text-muted-foreground" /><span className="text-xs text-muted-foreground">Desligado</span></>
                    )}
                  </div>
                </div>
                {!gcalConnected ? (
                  <Button onClick={async () => {
                    try {
                      const { data, error } = await supabase.functions.invoke("google-calendar-sync", {
                        body: { action: "get_auth_url", redirect_uri: `${window.location.origin}/admin/api` }
                      });
                      if (error) throw error;
                      if (data?.url) window.location.href = data.url;
                    } catch (e: any) {
                      toast({ title: "Erro ao ligar", description: e.message, variant: "destructive" });
                    }
                  }} className="gap-2">
                    Ligar Google Calendar
                  </Button>
                ) : (
                  <Button variant="outline" onClick={async () => {
                    await supabase.from("app_settings").delete().eq("key", "google_calendar_tokens");
                    setGcalConnected(false);
                    toast({ title: "Google Calendar desligado" });
                  }} className="text-destructive border-destructive/20 hover:bg-destructive/10">
                    Desligar
                  </Button>
                )}
              </div>

              {gcalConnected && (
                <div className="flex items-center justify-between p-4 rounded-xl border border-border">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Sincronização Bidirecional</p>
                    <p className="text-xs text-muted-foreground">Última sincronização: {gcalLastSync ? new Date(gcalLastSync).toLocaleString('pt-PT') : 'Nunca'}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={async () => {
                    setGcalSyncing(true);
                    try {
                      const { data, error } = await supabase.functions.invoke("google-calendar-sync", { body: { action: "sync" } });
                      if (error) throw error;
                      setGcalLastSync(new Date().toISOString());
                      toast({ title: "Sincronização concluída", description: "Agenda atualizada com o Google Calendar." });
                    } catch (e: any) {
                      toast({ title: "Erro na sincronização", description: e.message, variant: "destructive" });
                    } finally { setGcalSyncing(false); }
                  }} disabled={gcalSyncing} className="gap-2">
                    {gcalSyncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    Sincronizar Agora
                  </Button>
                </div>
              )}
            </div>
            
            <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 text-[11px] text-blue-700">
              💡 <strong>Como funciona:</strong> Ao ativar a sincronização, todas as novas reservas confirmadas no sistema são criadas no seu Google Calendar. Se alterar ou cancelar uma reserva no sistema, o evento no Google será atualizado. Da mesma forma, eventos criados no seu Google Calendar marcados como "Ocupado" bloquearão horários na agenda do sistema.
              <br/><br/>
              <strong>Nota:</strong> Esta integração serve apenas para sincronizar o calendário e não altera a forma como faz login no sistema.
            </div>
          </section>
        </TabsContent>

        {/* AI / Translation Tab */}
        <TabsContent value="ai" className="space-y-8">
          <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Sparkles size={20} className="text-purple-600" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold">Groq AI (Tradução)</h2>
                <p className="text-xs text-muted-foreground">
                  Configure a Groq para traduções ultra-rápidas e evitar falhas de rate limit. Se não configurar, usa o gateway Lovable AI por defeito.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">API Key da Groq</Label>
              <div className="flex gap-2">
                <Input
                  type={showGroqKey ? "text" : "password"}
                  value={groqApiKey}
                  onChange={(e) => { setGroqApiKey(e.target.value); setGroqValidated(false); }}
                  placeholder="gsk_..."
                  className="flex-1"
                />
                <Button type="button" variant="outline" size="icon" onClick={() => setShowGroqKey((v) => !v)}>
                  {showGroqKey ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Obtenha em <a href="https://console.groq.com/keys" target="_blank" rel="noreferrer" className="underline">console.groq.com/keys</a>
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={validateGroq} disabled={groqLoading || !groqApiKey.trim()} className="gap-1">
                {groqLoading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                Validar & Carregar modelos
              </Button>
              {groqValidated && (
                <span className="flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle size={14} /> Chave válida
                </span>
              )}
            </div>

            {groqModels.length > 0 && (
              <div className="space-y-2 pt-3 border-t border-border/50">
                <Label className="text-xs">Modelo</Label>
                <Select value={groqModel} onValueChange={setGroqModel}>
                  <SelectTrigger><SelectValue placeholder="Selecione um modelo" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {groqModels.filter((m) => m.tier === "free").length > 0 && (
                      <>
                        <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase">Free / Produção</div>
                        {groqModels.filter((m) => m.tier === "free").map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.id} {m.context_window ? `· ${Math.round(m.context_window / 1000)}k ctx` : ""}
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {groqModels.filter((m) => m.tier !== "free").length > 0 && (
                      <>
                        <div className="px-2 py-1 mt-1 text-[10px] font-semibold text-muted-foreground uppercase">Preview / Pagos</div>
                        {groqModels.filter((m) => m.tier !== "free").map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.id} {m.context_window ? `· ${Math.round(m.context_window / 1000)}k ctx` : ""}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  O modelo escolhido será usado automaticamente pela tradução do CMS. Não esqueça de clicar em "Guardar" no topo/rodapé para persistir.
                </p>
              </div>
            )}

            <div className="rounded-lg bg-muted/40 p-3 text-[11px] text-muted-foreground">
              💡 <strong>Dica:</strong> A Groq oferece tier gratuito generoso, os modelos <code>llama-3.3-70b-versatile</code> e <code>llama-3.1-8b-instant</code> são ideais para tradução rápida.
            </div>
          </section>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Guardar configurações
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="gcal" className="space-y-8">
          <section className="rounded-2xl border border-border bg-card p-6 space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Calendar size={20} className="text-blue-600" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold">Google Calendar</h2>
                <p className="text-xs text-muted-foreground">Sincronização bidirecional de reservas</p>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted/30">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Estado da Ligação</p>
                  <div className="flex items-center gap-2">
                    {gcalConnected ? (
                      <span className="flex items-center gap-1.5 text-xs text-green-600 font-medium">
                        <CheckCircle size={14} /> Ligado ao Google Calendar
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                        <XCircle size={14} /> Não ligado
                      </span>
                    )}
                  </div>
                </div>
                
                <Button 
                  variant={gcalConnected ? "outline" : "default"}
                  onClick={async () => {
                    if (gcalConnected) {
                      // Desligar logic (placeholder)
                      setGcalConnected(false);
                      toast({ title: "Google Calendar desligado" });
                      return;
                    }
                    const { data, error } = await supabase.functions.invoke("google-calendar-sync", {
                      body: { action: "get_auth_url", redirect_uri: `${window.location.origin}/admin/api` }
                    });
                    if (data?.url) window.location.href = data.url;
                    else toast({ title: "Erro ao iniciar ligação", variant: "destructive" });
                  }}
                  className="gap-2"
                >
                  <Calendar size={16} />
                  {gcalConnected ? "Desligar Conta" : "Ligar Google Calendar"}
                </Button>
              </div>

              {gcalConnected && (
                <div className="space-y-4 pt-2">
                  <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                    <span>Última sincronização: {gcalLastSync || "Nunca"}</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-7 text-xs gap-1.5"
                      disabled={gcalSyncing}
                      onClick={async () => {
                        setGcalSyncing(true);
                        try {
                          const { error } = await supabase.functions.invoke("google-calendar-sync", { body: { action: "sync" } });
                          if (error) throw error;
                          setGcalLastSync(new Date().toLocaleString());
                          toast({ title: "Sincronização concluída!" });
                        } catch (e) {
                          toast({ title: "Erro na sincronização", variant: "destructive" });
                        } finally {
                          setGcalSyncing(false);
                        }
                      }}
                    >
                      <RefreshCw size={12} className={gcalSyncing ? "animate-spin" : ""} />
                      Sincronizar Agora
                    </Button>
                  </div>
                  
                  <div className="rounded-lg bg-blue-50 p-3 text-[11px] text-blue-700 border border-blue-100">
                    <strong>Como funciona:</strong> Todas as reservas confirmadas são enviadas para o seu calendário. 
                    Bloqueios de horário no Google Calendar (marcados como "Ocupado") também impedem novos agendamentos no site.
                  </div>
                </div>
              )}
            </div>
          </section>
        </TabsContent>

        <TabsContent value="tracking" className="space-y-8">
          {/* Google Analytics */}
          <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <BarChart3 size={20} className="text-amber-600" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold">Google Analytics</h2>
                <p className="text-xs text-muted-foreground">Rastreio de visitas e comportamento dos utilizadores</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>ID de Medição (Measurement ID)</Label>
              <Input value={gaId} onChange={(e) => setGaId(e.target.value)} placeholder="G-XXXXXXXXXX" />
              <p className="text-[11px] text-muted-foreground">Encontre em Google Analytics &gt; Admin &gt; Fluxos de dados &gt; ID de medição</p>
            </div>
            <div className="flex items-center gap-2 text-xs">
              {gaId.trim() ? (
                <><CheckCircle size={14} className="text-green-500" /><span className="text-green-600">Configurado</span></>
              ) : (
                <><XCircle size={14} className="text-muted-foreground" /><span className="text-muted-foreground">Não configurado</span></>
              )}
            </div>
          </section>

          {/* Meta Pixel */}
          <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Facebook size={20} className="text-blue-600" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold">Meta Pixel (Facebook / Instagram Ads)</h2>
                <p className="text-xs text-muted-foreground">Rastreio de conversões para campanhas Meta Ads</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Pixel ID</Label>
              <Input value={metaPixelId} onChange={(e) => setMetaPixelId(e.target.value)} placeholder="123456789012345" />
              <p className="text-[11px] text-muted-foreground">Encontre em Meta Business Suite &gt; Gestor de Eventos &gt; Pixel</p>
            </div>
            <div className="flex items-center gap-2 text-xs">
              {metaPixelId.trim() ? (
                <><CheckCircle size={14} className="text-green-500" /><span className="text-green-600">Configurado</span></>
              ) : (
                <><XCircle size={14} className="text-muted-foreground" /><span className="text-muted-foreground">Não configurado</span></>
              )}
            </div>
          </section>

          {/* TikTok Pixel */}
          <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-pink-500/10 flex items-center justify-center">
                <BarChart3 size={20} className="text-pink-600" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold">TikTok Pixel</h2>
                <p className="text-xs text-muted-foreground">Rastreio de conversões para campanhas TikTok Ads</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Pixel ID</Label>
              <Input value={tiktokPixelId} onChange={(e) => setTiktokPixelId(e.target.value)} placeholder="CXXXXXXXXXXXXXXX" />
              <p className="text-[11px] text-muted-foreground">Encontre em TikTok Ads Manager &gt; Eventos &gt; Gerir &gt; Pixel ID</p>
            </div>
            <div className="flex items-center gap-2 text-xs">
              {tiktokPixelId.trim() ? (
                <><CheckCircle size={14} className="text-green-500" /><span className="text-green-600">Configurado</span></>
              ) : (
                <><XCircle size={14} className="text-muted-foreground" /><span className="text-muted-foreground">Não configurado</span></>
              )}
            </div>
          </section>

          {/* Dynamic Tracking Tags */}
          <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                <Code size={20} className="text-cyan-600" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold">Tags de Rastreio Personalizadas</h2>
                <p className="text-xs text-muted-foreground">Adicione tags de qualquer plataforma (Hotjar, LinkedIn, Pinterest, etc.)</p>
              </div>
            </div>
            <div className="space-y-3">
              {trackingTags.map((tag, idx) => (
                <div key={tag.id} className="rounded-xl border border-border p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Input
                      value={tag.name}
                      onChange={(e) => {
                        const updated = [...trackingTags];
                        updated[idx] = { ...updated[idx], name: e.target.value };
                        setTrackingTags(updated);
                      }}
                      placeholder="Nome (ex: Hotjar, LinkedIn Insight)"
                      className="text-sm flex-1"
                    />
                    <select
                      value={tag.placement}
                      onChange={(e) => {
                        const updated = [...trackingTags];
                        updated[idx] = { ...updated[idx], placement: e.target.value as "head" | "body" };
                        setTrackingTags(updated);
                      }}
                      className="text-xs border border-border rounded-lg px-2 py-2 bg-background"
                    >
                      <option value="head">Head</option>
                      <option value="body">Body</option>
                    </select>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setTrackingTags(trackingTags.filter((_, i) => i !== idx))}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  <Textarea
                    value={tag.code}
                    onChange={(e) => {
                      const updated = [...trackingTags];
                      updated[idx] = { ...updated[idx], code: e.target.value };
                      setTrackingTags(updated);
                    }}
                    placeholder={'<script>\n  // Cole o código da tag aqui\n</script>'}
                    rows={3}
                    className="font-mono text-xs"
                  />
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTrackingTags([...trackingTags, { id: crypto.randomUUID(), name: "", code: "", placement: "head", enabled: true }])}
                className="gap-1"
              >
                <Plus size={14} /> Adicionar Tag
              </Button>
            </div>
          </section>

          {/* Custom Head Scripts */}
          <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
                <Code size={20} className="text-violet-600" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold">Scripts Personalizados, Head</h2>
                <p className="text-xs text-muted-foreground">Código inserido dentro do &lt;head&gt; da página (ex: Google Tag Manager, Hotjar, etc.)</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Código &lt;script&gt; para o Head</Label>
              <Textarea
                value={customHeadScripts}
                onChange={(e) => setCustomHeadScripts(e.target.value)}
                placeholder={'<script async src="https://..."></script>'}
                rows={5}
                className="font-mono text-xs"
              />
            </div>
          </section>

          {/* Custom Body Scripts */}
          <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <Code size={20} className="text-emerald-600" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold">Scripts Personalizados, Body</h2>
                <p className="text-xs text-muted-foreground">Código inserido dentro do &lt;body&gt; (ex: código Messenger, chatbots, widgets)</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Código &lt;script&gt; para o Body</Label>
              <Textarea
                value={customBodyScripts}
                onChange={(e) => setCustomBodyScripts(e.target.value)}
                placeholder={'<script>\n  // Código do Messenger ou chatbot\n</script>'}
                rows={5}
                className="font-mono text-xs"
              />
              <p className="text-[11px] text-muted-foreground">Cole aqui o código &lt;script&gt; que o serviço pede para inserir dentro da tag &lt;body&gt;</p>
            </div>
          </section>
        </TabsContent>

        {/* Database Tab */}
        <TabsContent value="database" className="space-y-8">
          <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Database size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold">Base de Dados Principal</h2>
                <p className="text-xs text-muted-foreground">Credenciais da base de dados do sistema (Lovable Cloud)</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>URL da Base de Dados</Label>
                <Input value={dbUrl} onChange={(e) => setDbUrl(e.target.value)} placeholder="https://xxxxx.supabase.co" />
              </div>
              <div className="space-y-2">
                <Label>Chave Pública (Anon Key)</Label>
                <div className="flex gap-2">
                  <Input
                    type={showDbKey ? "text" : "password"}
                    value={dbAnonKey}
                    onChange={(e) => setDbAnonKey(e.target.value)}
                    placeholder="eyJhbGciOiJIUzI1NiIs..."
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" size="icon" onClick={() => setShowDbKey(!showDbKey)}>
                    {showDbKey ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs">
              {dbUrl.trim() && dbAnonKey.trim() ? (
                <><CheckCircle size={14} className="text-green-500" /><span className="text-green-600">Configurado</span></>
              ) : (
                <><XCircle size={14} className="text-muted-foreground" /><span className="text-muted-foreground">Não configurado</span></>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground"> Alterar estes valores requer reiniciar a aplicação para ter efeito.</p>
          </section>

          {/* Custom DB Connections */}
          <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
                <Database size={20} className="text-orange-600" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold">Conexões Personalizadas</h2>
                <p className="text-xs text-muted-foreground">Adicione bases de dados externas para integrações</p>
              </div>
            </div>
            <div className="space-y-3">
              {customDbConnections.map((conn, idx) => (
                <div key={conn.id} className="rounded-xl border border-border p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Input
                      value={conn.name}
                      onChange={(e) => {
                        const updated = [...customDbConnections];
                        updated[idx] = { ...updated[idx], name: e.target.value };
                        setCustomDbConnections(updated);
                      }}
                      placeholder="Nome da conexão (ex: CRM, ERP)"
                      className="text-sm flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setCustomDbConnections(customDbConnections.filter((_, i) => i !== idx))}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  <Input
                    value={conn.url}
                    onChange={(e) => {
                      const updated = [...customDbConnections];
                      updated[idx] = { ...updated[idx], url: e.target.value };
                      setCustomDbConnections(updated);
                    }}
                    placeholder="URL do servidor"
                    className="text-sm"
                  />
                  <Input
                    value={conn.key}
                    onChange={(e) => {
                      const updated = [...customDbConnections];
                      updated[idx] = { ...updated[idx], key: e.target.value };
                      setCustomDbConnections(updated);
                    }}
                    placeholder="Chave de acesso / API Key"
                    type="password"
                    className="text-sm"
                  />
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCustomDbConnections([...customDbConnections, { id: crypto.randomUUID(), name: "", url: "", key: "" }])}
                className="gap-1"
              >
                <Plus size={14} /> Adicionar Conexão
              </Button>
            </div>
          </section>
        </TabsContent>
      </Tabs>

      <div className="max-w-2xl mt-8">
        <Button onClick={handleSave} disabled={saving} className="w-full gap-2 rounded-full">
          <Save size={16} /> {saving ? "A guardar..." : "Guardar todas as configurações"}
        </Button>
      </div>
    </div>
  );
};

export default ApiSettings;
